import { defineStore } from 'pinia'

import api from '@/api/api'
import { StoreFloorPlan, parseFloorPlan } from '@/models'
import { byId, deleteById, gtmEvt, upsertById } from '@/utils/utils'

import { ArrangementRead, TableWrite } from '@generated/types'

export interface FloorPlanState {
  arrangements: StoreFloorPlan[]
}

export const useFloorPlanStore = defineStore({
  id: 'floorPlan',
  state: (): FloorPlanState => {
    return {
      arrangements: [] as StoreFloorPlan[],
    }
  },
  getters: {
    primaryFloorPlan(): StoreFloorPlan | undefined {
      return this.arrangements.find(arr => arr.is_primary)
    },
  },
  actions: {
    floorPlanById(id: number) {
      return this.arrangements.find(byId(id))
    },

    storeFloorPlan(arrangement: ArrangementRead): StoreFloorPlan {
      return upsertById(parseFloorPlan(arrangement), this.arrangements)
    },

    async fetchAll() {
      const response = await api.getFloorPlans()
      response.data.map(this.storeFloorPlan)
    },

    async addFloorPlan(name: any) {
      gtmEvt('addArrangement')
      const response = await api.addFloorPlan({
        name,
        is_primary: false,
      })
      return this.storeFloorPlan(response.data)
    },

    async updateFloorPlan({ id, name, is_primary }) {
      gtmEvt('updateArrangement')
      const response = await api.updateFloorPlan({
        id,
        name,
        is_primary,
      })
      return this.storeFloorPlan(response.data)
    },

    async duplicateFloorPlan(id: number): Promise<StoreFloorPlan> {
      gtmEvt('duplicateArrangement')
      const response = await api.duplicateFloorPlan(id)
      return this.storeFloorPlan(response.data)
    },

    async deleteFloorPlan(id: number) {
      gtmEvt('deleteArrangement')
      await api.deleteFloorPlan(id)
      deleteById(id, this.arrangements)
    },

    async deleteFloorPlanTable({ id, table_id }) {
      gtmEvt('deleteArrangementTable')
      await api.deleteFloorPlanTable(table_id)
      const arrangement = this.floorPlanById(id)
      if (arrangement) {
        deleteById(table_id, arrangement.tables)
      }
    },

    async upsertFloorPlanTable({
      id,
      table_id,
      name,
      minimum_guests,
      maximum_guests,
      x,
      y,
      h,
      w,
      shape,
      default_price_per_person,
      type_id,
      rotation,
      held,
      standing_room,
    }) {
      gtmEvt('upsertArrangementTable')
      const tableWrite: TableWrite = {
        id: table_id,
        name,
        minimum_guests,
        maximum_guests,
        x,
        y,
        h,
        w,
        shape,
        arrangement: id,
        default_price_per_person,
        type_id,
        rotation,
        held,
        standing_room,
      }

      if (!tableWrite.type_id) delete tableWrite.type_id
      const response = !table_id
        ? await api.addFloorPlanTable(tableWrite)
        : await api.updateFloorPlanTable(tableWrite)
      const table = response.data

      const floorPlan = this.floorPlanById(id)
      if (floorPlan) {
        upsertById(table, floorPlan.tables)
      }
      return table
    },

    async bulkPriceFloorPlanTables(floorPlanId: number, tables: number[], price: number) {
      gtmEvt('bulkPriceArrangementTables')
      const response = await api.bulkPriceFloorPlanTables(floorPlanId, { tables, price })
      const currentFloorPlan = this.floorPlanById(floorPlanId)
      tables.forEach(tableId => {
        const table = currentFloorPlan?.tables.find(table => table.id === tableId)
        if (table) {
          table.default_price_per_person = price
        }
      })
      return response.data
    },
  },
})
